import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicSignContractPage = () => {
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [signatureNationalId, setSignatureNationalId] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    fetchContractForSigning();
  }, [contractId]);

  const fetchContractForSigning = async () => {
    try {
      setLoading(true);
      // استخدام endpoint عام للتحقق من العقد
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/public/verify/${contractId}`
      );
      
      setContract(response.data);
      setError(null);
    } catch (err) {
      console.error('خطأ في جلب العقد:', err);
      setError(err.response?.data?.detail || 'حدث خطأ في جلب بيانات العقد');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (e) => {
    e.preventDefault();
    
    if (!signatureName.trim()) {
      alert('يرجى إدخال اسمك الكامل');
      return;
    }
    
    if (!signatureNationalId.trim()) {
      alert('يرجى إدخال رقم الهوية');
      return;
    }

    setSigning(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/signatures/public/sign/${contractId}`,
        {
          signer_name: signatureName,
          signer_national_id: signatureNationalId,
          signature_type: 'client'
        },
        {
          timeout: 30000 // 30 seconds timeout
        }
      );
      
      setSigned(true);
      alert('✅ تم توقيع العقد بنجاح!');
    } catch (err) {
      console.error('خطأ في التوقيع:', err);
      alert(err.response?.data?.detail || 'حدث خطأ في التوقيع. يرجى المحاولة مرة أخرى');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 mx-auto mb-4 text-[#C8A870] animate-spin" />
          <p className="text-gray-600">جاري تحميل العقد...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">خطأ في تحميل العقد</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">تم التوقيع بنجاح!</h2>
          <p className="text-gray-600 mb-4">
            تم توقيع العقد رقم <span className="font-mono font-bold">{contract.contract_number}</span> بنجاح
          </p>
          <p className="text-sm text-gray-500">
            سيتم إشعار الطرف الآخر بإتمام التوقيع
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] rounded-2xl shadow-lg p-8 mb-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <FileText className="w-12 h-12" />
            <div>
              <h1 className="text-3xl font-bold">توقيع العقد</h1>
              <p className="text-blue-100">منصة وازن الرقمية للخدمات القانونية</p>
            </div>
          </div>
        </div>

        {/* Contract Info */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">معلومات العقد</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">رقم العقد</p>
              <p className="font-mono font-bold text-gray-900">{contract.contract_number}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">تاريخ الإنشاء</p>
              <p className="font-bold text-gray-900">
                {new Date(contract.created_at).toLocaleDateString('ar-SA')}
              </p>
            </div>
          </div>
        </div>

        {/* Signature Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">نموذج التوقيع</h2>
          
          <form onSubmit={handleSign} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                الاسم الكامل *
              </label>
              <input
                type="text"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
                placeholder="أدخل اسمك الكامل"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                رقم الهوية الوطنية *
              </label>
              <input
                type="text"
                value={signatureNationalId}
                onChange={(e) => setSignatureNationalId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
                placeholder="أدخل رقم الهوية"
                required
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>ملاحظة:</strong> بالضغط على زر "توقيع العقد" أدناه، أنت توافق على جميع بنود وشروط العقد المذكورة.
              </p>
            </div>

            <button
              type="submit"
              disabled={signing}
              className={`w-full py-4 rounded-lg font-bold text-white transition-colors ${
                signing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#C8A870] to-[#b8986a] hover:from-[#b8986a] hover:to-[#C8A870]'
              }`}
            >
              {signing ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader className="w-5 h-5 animate-spin" />
                  جاري التوقيع...
                </span>
              ) : (
                'توقيع العقد'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2025 منصة وازن الرقمية - جميع الحقوق محفوظة</p>
        </div>
      </div>
    </div>
  );
};

export default PublicSignContractPage;
